import React from 'react';
import { LucideIcon } from 'lucide-react';
import { SearchInput } from 'back-office/shared/components/rolesUi';

interface SectionHeaderProps {
  icon: LucideIcon;
  title: string;
  /** Rend le titre cliquable (retour à la liste) quand un fil d'Ariane suit. */
  onTitleClick?: () => void;
  /** Suite du fil d'Ariane après le titre (ex. « / Distribution [badge] »). */
  breadcrumb?: React.ReactNode;
  subtitle: React.ReactNode;
  /** Boutons contextuels, rendus entre le sous-titre et la recherche. */
  actions?: React.ReactNode;
  /** Recherche épinglée tout à droite — même place dans toutes les sections. */
  search?: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
  };
}

/**
 * En-tête commun des sections de l'atelier (Rôles, Profils, Portails) : icône
 * de la section + titre (ou fil d'Ariane en vue détail) + sous-titre à gauche,
 * actions puis recherche à droite. Géométrie identique partout pour que rien
 * ne bouge en changeant de section ou en entrant dans un détail.
 */
export const SectionHeader: React.FC<SectionHeaderProps> = ({
  icon: Icon,
  title,
  onTitleClick,
  breadcrumb,
  subtitle,
  actions,
  search
}) => (
  <div className="flex flex-wrap items-end justify-between gap-3">
    <div>
      <nav aria-label="Fil d'Ariane" className="flex items-center gap-2 text-lg">
        <Icon className="h-5 w-5 text-primary" />
        {onTitleClick ? (
          <button
            type="button"
            onClick={onTitleClick}
            className="font-semibold text-gray-900 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
          >
            {title}
          </button>
        ) : (
          <h2 className="font-semibold text-gray-900">{title}</h2>
        )}
        {breadcrumb}
      </nav>
      <p className="mt-1 text-sm text-gray-500">{subtitle}</p>
    </div>
    <div className="flex items-center gap-3">
      {actions}
      {search && (
        <SearchInput
          className="w-64"
          placeholder={search.placeholder}
          value={search.value}
          onChange={search.onChange}
        />
      )}
    </div>
  </div>
);
